import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Grid,
  Box,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { keyframes } from "@mui/system";

/**
 * =====================================================
 * JAMB EXAM MODAL (4-STRAIGHT ONLY)
 * -----------------------------------------------------
 * - Question Map
 * - Submit THIS subject confirmation
 * - FINAL success modal (only when all subjects completed)
 *
 * ❌ No routing
 * ❌ No chaining logic
 * =====================================================
 */

/* ================= SUCCESS ANIMATION ================= */
const popIn = keyframes`
  0% { transform: scale(0.3); opacity: 0; }
  70% { transform: scale(1.15); opacity: 1; }
  100% { transform: scale(1); }
`;

export default function JambExamModal({
  /* ================= QUESTION MAP ================= */
  mapOpen,
  setMapOpen,
  questions = [],
  setIndexSafely,

  /* ================= SUBMIT CONFIRM ================= */
  confirmSubmit,
  setConfirmSubmit,
  answers = {},
  handleSubmit,

  /* ================= FINAL SUCCESS ================= */
  finalSuccessOpen,
  onViewAnalysis,

  /* ================= STATE ================= */
  submittingUI,
  chainError,

  /* ================= UI FLAGS ================= */
  isFinalSubject, // ✅ NEW
}) {
  const answeredCount = Object.keys(answers).length;

  return (
    <>
      {/* ==================================================
          QUESTION MAP
         ================================================== */}
      <Dialog
        open={mapOpen}
        onClose={() => setMapOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Question Map</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={1}>
            {questions.map((q, index) => (
              <Grid item xs={3} key={q.id}>
                <Button
                  fullWidth
                  variant={
                    answers[q.id] !== undefined ? "contained" : "outlined"
                  }
                  color={
                    answers[q.id] !== undefined ? "success" : "primary"
                  }
                  onClick={async () => {
                    await setIndexSafely(index);
                    setMapOpen(false);
                  }}
                >
                  {index + 1}
                </Button>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMapOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ==================================================
          SUBMIT CONFIRMATION (THIS SUBJECT ONLY)
         ================================================== */}
      <Dialog
        open={confirmSubmit}
        onClose={() => {
          if (!submittingUI) setConfirmSubmit(false);
        }}
        disableEscapeKeyDown={submittingUI}
        TransitionProps={{ unmountOnExit: true }}
      >
        <DialogTitle>
          {isFinalSubject ? "Final Submission" : "Submit This Subject?"}
        </DialogTitle>

        <DialogContent>
          <Typography>
            You have answered <strong>{answeredCount}</strong> question
            {answeredCount !== 1 ? "s" : ""}.
          </Typography>

          <Typography sx={{ mt: 1 }}>
            {isFinalSubject ? (
              <>
                This will <strong>submit your entire JAMB exam</strong>.
                <br />
                You will be able to view your final result.
              </>
            ) : (
              <>
                This will submit <strong>this subject only</strong>.
                <br />
                The next subject will start automatically.
              </>
            )}
          </Typography>

          {chainError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {chainError}
            </Alert>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setConfirmSubmit(false)}
            disabled={submittingUI}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            color={isFinalSubject ? "success" : "error"}
            disabled={submittingUI}
            startIcon={
              submittingUI ? <CircularProgress size={18} /> : null
            }
            onClick={() => handleSubmit(false)}
          >
            {isFinalSubject ? "Final Submit" : "Submit Subject"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ==================================================
          FINAL JAMB SUCCESS (ALL SUBJECTS COMPLETED)
         ================================================== */}
      <Dialog
        open={finalSuccessOpen}
        TransitionProps={{ unmountOnExit: true }}
      >
        <DialogContent sx={{ textAlign: "center", px: 4, py: 3 }}>
          <CheckCircleIcon
            sx={{
              fontSize: 90,
              color: "#22c55e",
              mb: 1,
              animation: `${popIn} 0.45s ease-out`,
            }}
          />

          <Typography variant="h5" fontWeight={900} gutterBottom>
            JAMB Exam Completed Successfully
          </Typography>

          <Typography sx={{ opacity: 0.85, mb: 3 }}>
            All subjects have been submitted successfully.
          </Typography>

          <Button
            variant="contained"
            color="success"
            sx={{ fontWeight: 800, minWidth: 220 }}
            onClick={onViewAnalysis}
          >
            View JAMB Result
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
}
